import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ExternalLink, Lock, Download, ChevronDown, History, RotateCcw, Monitor } from 'lucide-react';
import { osInstallGate } from '@platform/shared-types';
import { stageById, stageOrder, stageForSnapshotLabel } from './provisioningStages';
import {
  hardwareApi, formFactorLabel,
  type DeviceUnitRow, type DeviceModelRow, type ProvisioningTemplate,
} from '../../services/hardwareApi';
// What terminal type this catalog device provisions as — the same resolution
// the register panel uses, so the card and the register agree about the screen.
import { provisionTypeForModel } from '../pos/provisionType';
import { toastService } from '../../services/toastService';
import { useDataVersion } from '../../services/dataChangeBus';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * ONE DEVICE'S CONFIGURATION WORK — the OS gate, then the checklist.
 *
 * SHARED ON PURPOSE. This is drawn in two places: under the POS lane on an
 * order (`ProvisioningStagePanel`) and on the Pending configuration list at
 * /pos-config (`PendingConfigurationTab`). They are the same work seen from
 * two directions — the order asks "is this order ready to ship", the pending
 * list asks "what is on the bench today" — and a warehouse that ticks a step
 * in one place must see it ticked in the other.
 *
 * Two copies of this would have drifted the first time a rule changed, and
 * the rule that matters most here is a safety gate. So the component, the
 * lock, the stamp and the tick all live in this one file and both screens
 * import them.
 */

/**
 * WHY THIS DEVICE'S CHECKLIST IS LOCKED, or undefined when it is not.
 *
 * The rule itself lives in shared-types (`osInstallGate`) so this and the
 * service that refuses the tick cannot drift — a lock the screen draws
 * differently from the way the server applies it teaches people the screen is
 * lying.
 *
 * Read defensively, and DEGRADE TO LOCKED. `osInstallGate` is a runtime value
 * from the linked `@platform/shared-types`, and a Vite pre-bundle built before
 * that export existed hands back `undefined`; calling it at module scope would
 * white-screen the page. Falling back to "locked, and here is why" costs a
 * dev-server restart. Falling back to "unlocked" would quietly remove a safety
 * control and offer twenty ticks the server is going to refuse anyway — the
 * wrong direction to fail in.
 */
export const lockReason = (
  unit: DeviceUnitRow,
  model: DeviceModelRow | undefined,
): string | undefined => {
  if (typeof osInstallGate !== 'function') {
    console.error(
      '@platform/shared-types exported no osInstallGate — a stale Vite pre-bundle. RESTART the dev server: deleting node_modules/.vite under a running server does nothing, because Vite keeps the optimised bundle in memory and rewrites the directory.',
    );
    return 'the OS check could not be read — restart the dev server (see the console)';
  }
  return osInstallGate({ unit: unit as any, model: model as any });
};

/** What the stamp says, in one line: version, stage, batch. */
export const buildStamp = (u: DeviceUnitRow): string => {
  const os = u.osInstall;
  if (!os) return '';
  return [os.appBuild, os.source, os.target, os.hash ? `#${os.hash}` : '']
    .filter(Boolean).join(' · ');
};

/**
 * WHERE THE BUILD ON THIS DEVICE CAME FROM.
 *
 * `local` is the one that matters and the one the bare word hid: it means
 * somebody compiled it on their own machine, so there is no published
 * artifact, no CI record, and no way for anyone else to install the same
 * thing. That is fine on a bench and alarming on a till about to ship, and
 * the screen should say which it is rather than printing a lowercase word
 * that only the person who typed it recognises.
 */
const SOURCE_LABEL: Record<string, string> = {
  local: 'Built on an operator’s machine — not a published build',
  dev: 'Dev channel build',
  prod: 'Production build',
};
const sourceLabel = (v?: string): string => (v ? SOURCE_LABEL[v] || v : '—');

/** True when this device has an OS gate at all — i.e. it is a till. */
export const hasOsGate = (u: DeviceUnitRow, m: DeviceModelRow | undefined): boolean =>
  !!lockReason(u, m) || !!u.osInstall;

/**
 * THE STATES IN WHICH THE CHECKLIST IS BEHIND THE DEVICE, not ahead of it.
 *
 * `configured` is the meaningful one: the service moves a unit there at the
 * exact moment every REQUIRED step is done, and back to 'provisioning' if one
 * is unticked, so "configured" IS "the checklist is finished" — there is no
 * second flag to consult and no way for the two to disagree.
 *
 * The later states are included because a device that shipped obviously
 * finished; a register minted for one of those is a replacement, not a
 * premature one.
 *
 * `provisioning` is deliberately ABSENT, and that is the change: it used to be
 * here, which meant a register could be minted the moment somebody ticked step
 * one of twenty.
 */
const REGISTER_READY = new Set(['configured', 'packing', 'shipped', 'delivered', 'deployed']);

/**
 * WHY A REGISTER CANNOT BE CREATED FOR THIS DEVICE YET, or undefined when it
 * can.
 *
 * A REGISTER IS A CODE WITH AN EXPIRY. Minting one before the till can claim
 * it burns the window, and the old gate let that happen from step one of the
 * checklist while telling the operator to "install RubyTill first" — advice that
 * was already wrong, because the unit reached 'provisioning' precisely by
 * having work done on it.
 *
 * Three distinct situations, said apart, because they have three different
 * next actions: fetch a cable, finish the list, or start it.
 */
export const registerBlockReason = (
  unit: DeviceUnitRow,
  model: DeviceModelRow | undefined,
): string | undefined => registerGate(unit, model).reason;

/**
 * THE SAME GATE, COUNTED.
 *
 * `registerBlockReason` answers "why not" in a sentence, which is all a
 * tooltip needed. A panel that wants to say "1 more task and this unlocks",
 * and to show it getting closer as boxes are ticked, needs the NUMBERS — and
 * re-deriving them beside the sentence is how the two drift apart and start
 * disagreeing about what is left.
 *
 * So the counting lives here once and the sentence is built from it. `kind`
 * exists because the three blocks have three different next actions — fetch a
 * cable, finish the list, or start it — and only one of them is a countdown
 * anybody can watch.
 */
export interface RegisterGate {
  /** undefined when a register may be created now. */
  reason?: string;
  kind: 'ready' | 'install' | 'checklist' | 'not_started';
  /** Required steps still outstanding. 0 unless `kind` is 'checklist'. */
  pending: number;
  done: number;
  total: number;
}

export const registerGate = (
  unit: DeviceUnitRow,
  model: DeviceModelRow | undefined,
  /**
   * COUNT ONLY THE WORK THIS SCREEN SHOWS.
   *
   * A till carries ~20 steps spread across the whole journey, but a bench
   * screen draws only its own stages — the Pending queue shows configuring
   * and coupling, while testing, packing and the pre-ship checks are worked
   * on the ORDER, where the whole kit is together.
   *
   * Counting all of them here told an operator who had finished everything in
   * front of them that 6 tasks remained, with no way to see or reach them. It
   * also implied the wrong order of work: a register is a code the till
   * claims, and requiring the box to be PACKED before the code can be minted
   * is backwards.
   *
   * Omitted = count everything, which is what the unfiltered views want.
   */
  stageIds?: readonly string[],
): RegisterGate => {
  const noOs = lockReason(unit, model);
  if (noOs) {
    return {
      reason: 'Install RubyTill first — this till has no verified build on it.',
      kind: 'install', pending: 0, done: 0, total: 0,
    };
  }
  if (REGISTER_READY.has(unit.state)) return { kind: 'ready', pending: 0, done: 0, total: 0 };

  /* The SAME resolution the checklist draws with (`stageForSnapshotLabel` on a
     pinned step's group label), so the panel and its gate cannot disagree
     about which steps belong to this screen. */
  const inScope = (s: { category?: string }) => !stageIds
    || stageIds.includes(stageForSnapshotLabel(s.category || '', model)?.id || '');

  const required = (unit.provisioning?.steps || []).filter((s) => s.required && inScope(s));

  /* Every step this screen owns is ticked, but the unit has not reached
     `configured` because steps on OTHER screens are outstanding. The bench is
     done, so the bench says so and the register may be minted. */
  if (stageIds && required.length > 0 && required.every((s) => s.done)) {
    return { kind: 'ready', pending: 0, done: required.length, total: required.length };
  }
  if (required.length > 0) {
    const done = required.filter((s) => s.done).length;
    const pending = required.length - done;
    return {
      reason: `Finish the provisioning checklist first — ${done} of ${required.length} done.`,
      kind: 'checklist', pending, done, total: required.length,
    };
  }
  return {
    reason: 'Provisioning has not started on this device yet.',
    kind: 'not_started', pending: 0, done: 0, total: 0,
  };
};

const when = (ts?: number) =>
  ts ? new Date(ts).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

/**
 * OS STATUS AS A CHIP, for a dense table row.
 *
 * The list view has one line per device and no room for the card below, but
 * "blocked on the install" is exactly the thing somebody scanning that list needs
 * to see without opening anything — it decides whether they pick up a cable
 * or a checklist.
 */
export const OsStatusChip: React.FC<{ unit: DeviceUnitRow; model: DeviceModelRow | undefined }> = ({ unit, model }) => {
  const locked = lockReason(unit, model);
  if (!locked && !unit.osInstall) {
    // Not a till — no RubyTill to install, and an empty cell says that better than
    // a chip reading "n/a" on every cable and cash drawer.
    return <span className="text-[11.5px] text-gray-300">—</span>;
  }
  if (locked) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
        <Lock size={9} /> Not installed
      </span>
    );
  }
  return (
    <span
      className="inline-flex min-w-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"
      title={buildStamp(unit)}
    >
      <Check size={9} strokeWidth={3} />
      <span className="truncate">{unit.osInstall!.appBuild}</span>
    </span>
  );
};

/**
 * ─── DEVICE CONFIGURATION & OS INSTALL ───────────────────────────────────
 * THE STEP THAT COMES BEFORE THE PLAN, and is never ticked by hand.
 *
 * It is drawn as a step because that is what it is — the first piece of work
 * on a till — but it is the one step on either screen nobody can assert. It
 * completes when the installer reports a build, and the stamp is the
 * evidence: which version, which stage it came from, which artifact. That is
 * the difference between "somebody says they installed it" and "this machine
 * is running that binary".
 *
 * Drawn ABOVE the checklist rather than inside it so the order is the order
 * of the work, and left out of the "n of m" count deliberately: a
 * precondition is not a plan step, and folding it into the denominator would
 * make a device with no OS look 1/21 of the way done.
 */
/**
 * HOW MANY TIMES THIS BOX HAS BEEN BUILT, AND WHEN.
 *
 * `osInstall` holds only the LATEST install — it is overwritten each time —
 * so the card could say what is on the device now but never how it got there.
 * A till on its third build because two failed is a different object from one
 * installed cleanly first time, and that difference is exactly what somebody
 * is looking for when a device keeps coming back.
 *
 * Read from the unit's own timeline, which already records every install and
 * every reinstall request and never overwrites either. Fetched only when this
 * card is on screen — it is inside an expanded row, so it costs a request per
 * device somebody deliberately opened, not one per row in the table.
 *
 * FAILS SILENT. This is context beside a stamp that is already correct; a
 * broken history must not make an installed device look un-installed.
 */
/**
 * WHAT KIND OF TILL THIS IS, IN WORDS.
 *
 * Two sources answer it and they are not the same question. The CATALOG says
 * what this model is meant to be; the INSTALL TARGET says what the installer
 * actually built it as. They agree almost always, and when they agree saying
 * it twice is noise — so one line.
 *
 * WHEN THEY DISAGREE, THAT IS THE STORY. A device built as a single-screen
 * profile while the catalog calls it dual-screen will come up without its
 * customer display and nobody will know why. Showing the raw id was no help
 * with that; showing both, named and flagged, is.
 *
 * The exact target id stays on `title` — it is what the installer was given
 * and what anyone re-running it will type.
 */
const ScreenType: React.FC<{ unit: DeviceUnitRow; model: DeviceModelRow | undefined }> = ({ unit, model }) => {
  const target = unit.osInstall?.target;
  const catalog = provisionTypeForModel(model);
  const built = target ? formFactorLabel(target) : undefined;
  const expected = formFactorLabel(catalog);
  const mismatch = !!target && !!catalog && target !== catalog;

  return (
    <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-stone-600">
      <span className="inline-flex items-center gap-1 font-semibold text-stone-700" title={target || catalog}>
        <Monitor size={10} className="text-stone-400" />
        {built || expected}
      </span>
      {mismatch && (
        <span
          className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
          title={`Installed as ${target}, but the catalog calls this model ${catalog}`}
        >
          built as this, catalog says {expected}
        </span>
      )}
      {unit.osVersion && (
        <>
          <span className="text-stone-300">·</span>
          <span>Android {unit.osVersion}</span>
        </>
      )}
    </p>
  );
};

const INSTALL_KINDS = new Set(['os_installed', 'build_installed']);

const InstallHistory: React.FC<{ unitId: string }> = ({ unitId }) => {
  const [items, setItems] = useState<{ at: number; kind: string; label: string; actor?: string }[] | null>(null);
  const [open, setOpen] = useState(false);
  const dataVersion = useDataVersion();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await hardwareApi.getUnitTimeline(unitId, { limit: 100 });
        if (!alive) return;
        setItems((res.items || [])
          .filter((e) => INSTALL_KINDS.has(e.kind) || e.kind === 'reinstall_requested')
          .sort((a, b) => b.at - a.at));
      } catch {
        if (alive) setItems((cur) => cur ?? []);
      }
    })();
    return () => { alive = false; };
  }, [unitId, dataVersion]); // dataVersion: re-read after any save on the page

  if (!items || items.length === 0) return null;

  /* `build_installed` and `os_installed` are two events for ONE install — the
     app build and the OS version are recorded separately — so counting rows
     would double it. Counting distinct MOMENTS is what a person means. */
  const installMoments = new Set(
    items.filter((e) => INSTALL_KINDS.has(e.kind)).map((e) => Math.round(e.at / 60_000)),
  ).size;
  const reinstalls = items.filter((e) => e.kind === 'reinstall_requested').length;

  return (
    <div className="mt-2 border-t border-emerald-200/70 pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex min-h-[28px] items-center gap-1.5 text-[11px] font-semibold text-emerald-800 hover:underline"
      >
        <History size={11} />
        {installMoments} install{installMoments === 1 ? '' : 's'}
        {reinstalls > 0 && ` · ${reinstalls} reinstall${reinstalls === 1 ? '' : 's'} requested`}
        <ChevronDown size={11} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <ul className="mt-1.5 space-y-1">
          {items.map((e) => (
            <li key={`${e.at}-${e.kind}`} className="flex items-start gap-1.5 text-[10.5px] leading-snug text-stone-600">
              {e.kind === 'reinstall_requested'
                ? <RotateCcw size={10} className="mt-[3px] shrink-0 text-amber-600" />
                : <Check size={10} className="mt-[3px] shrink-0 text-emerald-600" />}
              <span className="min-w-0">
                <span className="text-stone-700">{e.label}</span>
                <span className="block text-stone-400">
                  {when(e.at)}{e.actor ? ` · ${e.actor}` : ''}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const OsInstallCard: React.FC<{
  unit: DeviceUnitRow;
  model: DeviceModelRow | undefined;
  stepCount: number;
}> = ({ unit, model, stepCount }) => {
  const locked = lockReason(unit, model);
  if (!locked && !unit.osInstall) return null;

  return (
    <div className={`rounded-lg border px-3 py-2.5 ${
      locked ? 'border-amber-300 bg-amber-50/80' : 'border-emerald-200 bg-emerald-50/60'}`}
    >
      <div className="flex items-start gap-2.5">
        <span className={`mt-[2px] grid h-[16px] w-[16px] shrink-0 place-items-center rounded-[4px] text-white ${
          locked ? 'border border-amber-400 bg-white' : 'bg-emerald-500'}`}
        >
          {locked ? <Lock size={9} className="text-amber-600" /> : <Check size={9} strokeWidth={3} />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[12.5px] font-semibold text-stone-900">
            Configure device &amp; install RubyTill
            <span className="font-normal text-stone-500">{locked ? ' · required first' : ' · done'}</span>
          </p>

          {locked ? (
            <>
              <p className="mt-0.5 text-[11.5px] leading-relaxed text-stone-600">
                This till has no verified build on it. The {stepCount} step{stepCount === 1 ? '' : 's'} below
                stay locked until it reports one — install it from the Install tab and this completes itself.
              </p>
              <Link
                to={`/pos-config?tab=install&unitId=${encodeURIComponent(unit.unitId)}`}
                className="mt-2 inline-flex min-h-[34px] items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-rose-700"
              >
                <Download size={11} /> Install RubyTill <ExternalLink size={10} />
              </Link>
            </>
          ) : (
            <>
              {/*
                LABELLED FACTS, NOT A STAMP.

                This was one mono line — "2026.09.20+555 · local ·
                android_dual_screen" — which is three different facts with
                nothing saying which is which. It is legible only to somebody
                who already knows the format, and it is read by people setting
                up a till for the first time. Each fact now says what it is.
              */}
              <dl className="mt-1.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[11px]">
                <dt className="text-stone-500">Version</dt>
                <dd className="break-all font-mono font-semibold text-emerald-800">{unit.osInstall!.appBuild}</dd>
                <dt className="text-stone-500">Build</dt>
                <dd className="text-stone-700">{sourceLabel(unit.osInstall!.source)}</dd>
              </dl>
              {/*
                THE SAME FACTS IN WORDS.

                The line above is what the installer wrote and has to stay
                verbatim — it is the thing you compare against a build. But
                `android_dual_screen` is a target id, and nobody at a bench
                should have to know that it means a till with a customer
                display. The screen type is the catalog's own answer, resolved
                the same way the register panel resolves it, so the card and
                the register it unlocks cannot disagree about the screen.
              */}
              <ScreenType unit={unit} model={model} />
              <p className="mt-0.5 text-[11px] text-stone-500">
                installed {when(unit.osInstall!.installedAt)} by {unit.osInstall!.by}
                {unit.osInstall!.serial ? ` · ${unit.osInstall!.serial}` : ''}
              </p>
              {/*
                BLANK IS A SEPARATE CLAIM FROM INSTALLED.
                A reinstall keeps the app's data unless it is wiped, so a till
                can carry a fresh build AND its previous tenant's pairing. Only
                the first line is about the binary; this one is about whether
                the device can be sent anywhere.
              */}
              {unit.osInstall!.reset === true && (
                <p className="mt-0.5 text-[11px] font-semibold text-emerald-700">
                  Total reset — no previous pairing, free to assign to any tenant or location.
                </p>
              )}
              {unit.osInstall!.reset === false && (
                <p className="mt-0.5 text-[11px] font-semibold text-amber-700">
                  Data kept — this device may still hold its previous pairing.
                </p>
              )}
              <InstallHistory unitId={unit.unitId} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

interface ChecklistProps {
  unit: DeviceUnitRow;
  model: DeviceModelRow | undefined;
  /**
   * The model's plan. Supply it when the caller already holds it — the order
   * panel fetches every model's template up front — and leave it undefined to
   * have this component fetch its own, which is what the pending list does so
   * that opening one row costs one request instead of loading every plan on
   * the page for rows nobody expands.
   */
  template?: ProvisioningTemplate | null;
  /** The unit as the service just returned it, so no caller has to refetch. */
  onUnitChanged?: (unit: DeviceUnitRow) => void;
  /** Fallback for callers with no single-unit update path. */
  onChanged?: () => void;
  /**
   * SHOW ONLY THESE STAGES.
   *
   * The whole reason steps are filed under a journey stage: a stage panel on
   * the order screen asks for its own stage and gets the work due at that
   * moment for this device — not the device's entire plan repeated under
   * every stage. Omitted means the whole checklist, which is what the bench
   * view (/pos-config) and the unit page want.
   */
  stageIds?: readonly string[];
  /** What to say when this device has no steps at the asked-for stage. */
  emptyNote?: string;
}

/**
 * THE CHECKLIST, TICKED HERE.
 *
 * The state is never written directly: the service moves the unit to
 * 'configured' when every required step is done, and back when one is
 * unticked. The checklist IS the flag.
 */
export const UnitChecklist: React.FC<ChecklistProps> = ({
  unit, model, template: given, onUnitChanged, onChanged, stageIds, emptyNote,
}) => {
  const [fetched, setFetched] = useState<ProvisioningTemplate | null | undefined>(undefined);
  const [ticking, setTicking] = useState<string | null>(null);
  /**
   * Which checklist sections the operator has opened or closed BY HAND. Only
   * hand overrides are stored; everything else falls back to the default
   * below, so the sections do not all spring open again on every tick (each
   * tick returns a new unit and re-renders these rows).
   */
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  const needsFetch = given === undefined;
  useEffect(() => {
    if (!needsFetch) return;
    let alive = true;
    (async () => {
      try {
        const res = await hardwareApi.getProvisioningTemplate(unit.modelId);
        const t = res.template;
        // A template with no steps is not a plan — treat it as none, so the
        // screen says so instead of showing an empty checklist as complete.
        if (alive) setFetched(t && (t.steps || []).length > 0 ? t : null);
      } catch {
        if (alive) setFetched(null);
      }
    })();
    return () => { alive = false; };
  }, [needsFetch, unit.modelId]);

  const template = needsFetch ? fetched : given;
  const snapshot = unit.provisioning;
  const steps = snapshot?.steps || [];
  const locked = lockReason(unit, model);

  /*
    THE FIRST TICK STARTS THE PLAN.

    Provisioning RUNS nothing — it is a checklist, and `startProvisioning`
    only copies the model's template onto the unit so the ticks have somewhere
    to land. Making that a button of its own implied a process kicking off, so
    it happens on the first tick instead.
  */
  const tick = useCallback(async (stepId: string, done: boolean) => {
    setTicking(stepId);
    let was = unit.state;
    try {
      if (!unit.provisioning) {
        const started = await hardwareApi.startProvisioning(unit.unitId);
        if (started?.unit) { was = started.unit.state; onUnitChanged?.(started.unit); }
      }
      const res = await hardwareApi.tickProvisioningStep(unit.unitId, stepId, done);
      const now = res?.unit?.state;
      if (now === 'configured' && was !== 'configured') {
        toastService.success('Every required step is done — this device is now Configured.');
      } else if (was === 'configured' && now === 'provisioning') {
        toastService.info('A required step was unticked, so this device is back to Provisioning.');
      }
      if (res?.unit && onUnitChanged) onUnitChanged(res.unit); else onChanged?.();
    } catch (e: any) {
      toastService.error(e?.message || 'Could not update that step.');
    } finally {
      setTicking(null);
    }
  }, [unit, onUnitChanged, onChanged]);

  if (needsFetch && fetched === undefined) {
    return (
      <RubyLoader variant="inline" label="reading the plan…" />
    );
  }

  /**
   * A preview step carries a `categoryId`; the snapshot carries the category's
   * LABEL. Resolving it here is what lets a not-yet-started unit show the same
   * Configuring / Testing / Packing sections as one already in flight.
   */
  const catLabel = new Map((template?.categories || []).map((c) => [c.id, c.label]));

  /**
   * The rows to draw: the unit's own pinned steps, or the model's plan as a
   * preview. Each carries the STAGE it is worked at.
   *
   * A pinned step stores its group's LABEL — that is what the service copies
   * onto the unit — so the stage has to be recovered from it. Two lanes share
   * the label "Configure hardware", which is why the model is part of the
   * lookup: a unit has exactly one lane, so the pair is unambiguous. A
   * preview step still has the real id and needs no guessing.
   */
  const rows = snapshot
    ? steps.map((st) => ({
      ...st, pinned: true, stageId: stageForSnapshotLabel(st.category || '', model)?.id,
    }))
    : (template?.steps || []).map((st) => ({
      stepId: st.stepId, label: st.label, category: catLabel.get(st.categoryId) || '', required: st.required,
      done: false, instructions: st.instructions, by: undefined, at: undefined, pinned: false,
      stageId: st.categoryId,
    }));

  /*
    A STAGE PANEL ASKS FOR ITS OWN STEPS AND GETS NOTHING ELSE. A step whose
    stage could not be resolved — written under a group that predates the
    stage list — is left out of a filtered view rather than shown at every
    stage, but stays visible in the unfiltered bench view so it is never lost
    silently.
  */
  const visible = stageIds ? rows.filter((r) => r.stageId && stageIds.includes(r.stageId)) : rows;

  /*
    A REAL TILL CARRIES ~20 STEPS, and twenty checkboxes in a row is a wall
    somebody scrolls past rather than works. Every step is filed under a stage
    of the order, so the checklist is drawn as those stages — in the order the
    work happens, each collapsed and showing how much of it is left.

    Sorted by JOURNEY position, never alphabetically: Packing before Testing
    would describe a different factory.
  */
  type Row = (typeof rows)[number];
  const sections: { key: string; label: string; rows: Row[] }[] = [];
  for (const st of visible) {
    const key = st.stageId || st.category || 'Steps';
    const found = sections.find((s) => s.key === key);
    if (found) found.rows.push(st);
    else sections.push({ key, label: stageById(key)?.label || st.category || 'Steps', rows: [st] });
  }
  sections.sort((a, b) => stageOrder(a.key) - stageOrder(b.key));

  /** The first section with work left is open; the rest start closed. */
  const firstUnfinished = sections.find((s) => s.rows.some((r) => !r.done))?.key;
  const isOpen = (key: string) =>
    // A single section is only left open while it is short enough to read —
    // otherwise collapsing has bought nothing.
    openSections[key] ?? ((sections.length === 1 && visible.length <= 8) || key === firstUnfinished);
  const allOpen = sections.every((s) => isOpen(s.key));
  const setAll = (open: boolean) =>
    setOpenSections(Object.fromEntries(sections.map((s) => [s.key, open])));

  /*
    THE OS GATE BELONGS TO ONE STAGE. It is the till's install, so it is drawn
    on the unfiltered view and at the POS configure stage — not above the
    packing steps, where it is neither the work nor the blocker being asked
    about.
  */
  const showOsCard = !stageIds || stageIds.includes('provisioned');

  return (
    <div className="space-y-2">
      {showOsCard && <OsInstallCard unit={unit} model={model} stepCount={visible.length} />}

      {stageIds && visible.length === 0 ? (
        <p className="text-[12px] leading-relaxed text-stone-500">
          {emptyNote || 'Nothing on this device’s checklist for this stage.'}
        </p>
      ) : !template && !snapshot ? (
        <p className="text-[12px] leading-relaxed text-stone-500">
          No plan for this model — a device configured against an empty checklist reads as "checked" and is not.
        </p>
      ) : (
        <>
          {!snapshot && (
            <p className="text-[12px] leading-relaxed text-stone-600">
              {locked
                ? 'Locked until the OS is on this device. Nothing here can be ticked yet — the service refuses it too, so this is not a display rule you can work around.'
                : 'Not started. Tick the first step and the plan is pinned to this device, so a later edit to the model\'s template cannot move it.'}
            </p>
          )}

          {sections.length > 1 && (
            <div className="flex items-center justify-between gap-3 text-[11px]">
              <span className="text-stone-500">
                <b className="font-mono text-stone-800">{visible.filter((r) => r.done).length}</b>
                <span className="text-stone-400"> / {visible.length}</span> steps done
              </span>
              <button
                type="button"
                onClick={() => setAll(!allOpen)}
                className="font-semibold text-stone-500 hover:text-stone-900"
              >
                {allOpen ? 'Collapse all' : 'Expand all'}
              </button>
            </div>
          )}

          <div className="space-y-2">
          {sections.map((sec) => {
            const open = isOpen(sec.key);
            const done = sec.rows.filter((r) => r.done).length;
            const complete = done === sec.rows.length;
            return (
            <div key={sec.key} className="overflow-hidden rounded-lg border border-stone-200 bg-white">
              <button
                type="button"
                onClick={() => setOpenSections((prev) => ({ ...prev, [sec.key]: !open }))}
                aria-expanded={open}
                className="flex min-h-[44px] w-full items-center gap-2.5 bg-stone-50 px-3 text-left hover:bg-stone-100"
              >
                <ChevronDown size={13} className={`shrink-0 text-stone-400 transition-transform ${open ? '' : '-rotate-90'}`} />
                <span className="min-w-0 flex-1 truncate text-[12px] font-bold text-stone-800">{sec.label}</span>
                <span className={`shrink-0 rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold ${
                  complete ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-600'}`}
                >
                  {done}/{sec.rows.length}
                </span>
              </button>
              {open && (
              <div className="space-y-1 p-2">
            {sec.rows.map((st) => (
              <button
                key={st.stepId}
                type="button"
                onClick={() => tick(st.stepId, !st.done)}
                /* Locked blocks TICKING, never UNticking — the service draws
                   the line in the same place. A correction made after the lock
                   appeared must not need an install to undo, or a wrong tick
                   becomes permanent. */
                disabled={(!!locked && !st.done) || ticking === st.stepId}
                title={!st.done ? (locked || undefined) : undefined}
                aria-pressed={!!st.done}
                className={`flex w-full min-h-[44px] items-start gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-colors disabled:opacity-60 ${
                  st.done ? 'border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50'
                    : 'border-stone-200 bg-white hover:bg-stone-50 disabled:hover:bg-white'}`}
              >
                {/* AN UNDONE STEP IS NOT A FAILED ONE — undone is simply an
                    empty box; "required" is said in words beside the label. */}
                <span className={`mt-[2px] grid h-[16px] w-[16px] shrink-0 place-items-center rounded-[4px] text-[10px] text-white ${
                  st.done ? 'bg-emerald-500' : 'border border-stone-300 bg-white'}`}
                >
                  {ticking === st.stepId
                    ? <RubyGemMark size={9} drawing color="currentColor" />
                    : st.done ? <Check size={9} strokeWidth={3} /> : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px] text-stone-800">
                    {st.label}
                    <span className="text-stone-400">{st.required ? ' · required' : ' · optional'}</span>
                  </span>
                  {/* The category is the section header now, so the row only
                      carries who ticked it and when. */}
                  {st.done && (st.by || st.at) && (
                    <span className="block text-[11px] text-stone-400">
                      {[st.by, st.at ? when(st.at) : null].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  {st.instructions && !st.done && (
                    <span className="mt-0.5 block text-[11px] leading-relaxed text-stone-500">{st.instructions}</span>
                  )}
                </span>
              </button>
            ))}
              </div>
              )}
            </div>
            );
          })}
          </div>
        </>
      )}
    </div>
  );
};

export default UnitChecklist;
